/**
 * What separates the five finance vouchers from one another.
 *
 * They are one screen, not five: a Cash Payment differs from a Bank Receipt in
 * which account the contra line lands on and which side it lands on. Everything
 * else — the balance rule, the period lock, FX, the approval gate — is
 * identical, and writing it five times would guarantee five slightly different
 * implementations of the balance rule.
 *
 * The instrument block (type, number, issue date, clearing date) used to be the
 * third difference: BP and BR had it, the other three did not. `0020` dropped
 * those columns from `voucher_lines`, so a bank voucher is now exactly a cash
 * voucher pointed at a bank account.
 *
 * Spec: `docs/reference/accounting-application-spec.md` §3.1.
 */

export type VoucherTypeCode = 'JV' | 'CP' | 'CR' | 'BP' | 'BR';

export type Side = 'Dr' | 'Cr';

export interface VoucherTypeConfig {
  code: VoucherTypeCode;
  label: string;
  description: string;
  /**
   * `JOURNAL` — a free-form grid with both a debit and a credit column, where
   * the accountant is responsible for the balance.
   *
   * `CONTRA` — the cash or bank account is chosen once in the header and its
   * line is generated; the grid holds only the analysis side. The voucher
   * cannot be entered out of balance because there is nothing to unbalance.
   */
  shape: 'JOURNAL' | 'CONTRA';
  contra?: {
    /** Which control kind the header picker offers. The accounts themselves come
     *  from the chart of accounts at render time, not from a constant here. */
    kind: 'CASH' | 'BANK';
    /** The side the generated cash/bank line lands on. Fixed by the type. */
    side: Side;
    label: string;
    /**
     * Whether the header offers the other accounts of this kind.
     *
     * On for BP and BR, because this book keeps four bank accounts and a bank
     * payment can come out of any of them: the first is the default the header
     * opens on, not the only answer. Off for CP and CR, where the till is one
     * account and a picker would be a question with a single answer — which is
     * why the field was taken off the header in the first place.
     *
     * **Which account it opens on is not written down here**, and used not to be
     * true: this interface carried `defaultAccountCode`, `'1111'` for the till
     * and `'1121'` for the bank. Those were the seeded codes, 0037 renumbered
     * both, and a literal was wrong anyway in any book whose chart was built
     * rather than seeded — invisibly, as "no account 1121 in this chart" against
     * a code nobody chose and no picker offered. `defaultAccountOfKind()` reads
     * the chart instead, the same registry `accountsOfKind()` fills, so a bank
     * opened in Setup is both selectable and defaultable with no change here.
     */
    pickable?: boolean;
  };
  /** The side every grid line lands on. Unused when `shape` is `JOURNAL`. */
  analysisSide: Side;
  /** Column header for the amount column on a `CONTRA` voucher. */
  amountLabel: string;
  narrationPlaceholder: string;
  /**
   * Maker–checker threshold in base currency. Above it, posting requires an
   * approver — spec §3.4, "configurable per voucher type and amount threshold".
   */
  approvalThreshold: string;
  /** Sentence under the line grid, describing the expected posting. */
  lineHelp: string;
}

export const VOUCHER_TYPES: Record<VoucherTypeCode, VoucherTypeConfig> = {
  JV: {
    code: 'JV',
    label: 'Journal Voucher',
    description: 'Enter debits and credits by hand; the two sides must add up to the same total.',
    shape: 'JOURNAL',
    analysisSide: 'Dr',
    amountLabel: 'Amount',
    narrationPlaceholder: 'Freight accrual — Lot IMP-26-118',
    approvalThreshold: '1000000.0000',
    lineHelp:
      'A control account takes no hand-written JV line — that would silently break the sub-ledger reconciliation, and trg_voucher_lines_derive rejects it for JV specifically. Where one has been broken out into 5th-level sub-accounts, choose the head and then the customer, supplier or salesperson it posts to: the line lands on the sub-account, and the sub-ledger still reconciles.',
  },
  CP: {
    code: 'CP',
    label: 'Cash Payment',
    description: 'Record money paid out of the cash till.',
    shape: 'CONTRA',
    contra: { kind: 'CASH', side: 'Cr', label: 'Paid from' },
    analysisSide: 'Dr',
    amountLabel: 'Payment',
    narrationPlaceholder: 'Warehouse labour — Karachi, week 30',
    approvalThreshold: '250000.0000',
    lineHelp:
      'Debit what the cash bought: an expense, an advance, or Accounts Payable when it clears a supplier bill. Set the party in the header for the last of those.',
  },
  CR: {
    code: 'CR',
    label: 'Cash Receipt',
    description: 'Record money received into the cash till.',
    shape: 'CONTRA',
    contra: { kind: 'CASH', side: 'Dr', label: 'Received into' },
    analysisSide: 'Cr',
    amountLabel: 'Receipt',
    narrationPlaceholder: 'Cash received against INV-26-004460',
    approvalThreshold: '1000000.0000',
    lineHelp:
      'A receipt credits Accounts Receivable directly — the control-account restriction applies to JV only, because this is the document the sub-ledger expects.',
  },
  BP: {
    code: 'BP',
    label: 'Bank Payment',
    description: 'Record money paid out of a bank account.',
    shape: 'CONTRA',
    contra: {
      kind: 'BANK',
      side: 'Cr',
      label: 'Paid from',
      pickable: true,
    },
    analysisSide: 'Dr',
    amountLabel: 'Payment',
    narrationPlaceholder: 'Punjab Oil Mills — PUR-26-000318 — cheque 004417',
    approvalThreshold: '500000.0000',
    lineHelp:
      'Debit what the payment settles: Accounts Payable with the party set to clear a supplier bill, or an expense directly. Put the cheque or transfer reference in the narration — the ledger no longer keeps a column for it.',
  },
  BR: {
    code: 'BR',
    label: 'Bank Receipt',
    description: 'Record money received into a bank account.',
    shape: 'CONTRA',
    contra: {
      kind: 'BANK',
      side: 'Dr',
      label: 'Received into',
      pickable: true,
    },
    analysisSide: 'Cr',
    amountLabel: 'Receipt',
    narrationPlaceholder: 'Received — Meezan 0192 — TT ref 88410-KWN',
    approvalThreshold: '1000000.0000',
    lineHelp:
      'A receipt credits Accounts Receivable directly — the control-account restriction applies to JV only. Record the transfer or cheque reference in the narration.',
  },
};

export const VOUCHER_TYPE_CODES = Object.keys(VOUCHER_TYPES) as VoucherTypeCode[];

export function isVoucherTypeCode(key: string): key is VoucherTypeCode {
  return key.toUpperCase() in VOUCHER_TYPES;
}
